import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import type { LeaderboardEntry } from '../types';

const GOLD = '#D4AF37';
const GOLD_LIGHT = '#E5C65C';
const CHARCOAL = '#1A1A1A';
const LEATHER = '#3D2B1F';

interface RevenueChartProps {
  data: LeaderboardEntry[];
  quarter?: string;
}

export default function RevenueChart({ data, quarter }: RevenueChartProps) {
  const chartData = data.map((e) => ({
    name: e.name.split(' ')[0] || e.name,
    fullName: e.name,
    revenue: e.total_revenue ?? 0,
  }));

  if (chartData.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-station-muted border border-leather rounded-lg bg-leather/20">
        No producer data for this quarter
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-leather bg-leather/40 p-5">
      {quarter && (
        <p className="text-station-muted text-sm mb-3">Quarter: {quarter}</p>
      )}
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 12, right: 12, left: 0, bottom: 24 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={LEATHER} opacity={0.6} />
            <XAxis
              dataKey="name"
              tick={{ fill: GOLD, fontSize: 12 }}
              axisLine={{ stroke: GOLD, opacity: 0.5 }}
              tickLine={{ stroke: GOLD, opacity: 0.5 }}
            />
            <YAxis
              tick={{ fill: GOLD, fontSize: 12 }}
              axisLine={{ stroke: GOLD, opacity: 0.5 }}
              tickLine={{ stroke: GOLD, opacity: 0.5 }}
              tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: CHARCOAL,
                border: `1px solid ${GOLD}`,
                borderRadius: 8,
              }}
              labelStyle={{ color: GOLD }}
              formatter={(value: number) => [`$${value.toLocaleString()}`, 'Revenue']}
              labelFormatter={(_, payload) => payload[0]?.payload?.fullName ?? ''}
            />
            <Bar dataKey="revenue" radius={[4, 4, 0, 0]} maxBarSize={80}>
              {chartData.map((_, i) => (
                <Cell
                  key={i}
                  fill={i === 0 ? GOLD : i === 1 ? GOLD_LIGHT : 'rgba(212, 175, 55, 0.6)'}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
